import {Tabs} from '@mantine/core';
import {Center} from 'src/design/atoms/Center';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import React, {useRef, useState} from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import type {InspectionScopeDeviceResponse} from 'src/api/ProactApiClient';
import {
  FeatureFlagEnum,
  InspectionStateEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useGetInspectionQuery} from 'src/redux/queries/inspection';
import {useGetInspectionScopeDevicesQuery} from 'src/redux/queries/inspectionRecord';
import {useGetSiteQuery} from 'src/redux/queries/site';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {BackBar} from 'src/app/layout/BackBar';
import {OfflineStatusPill} from 'src/app/offline/OfflineStatusPill';
import {useInspectionBreadcrumbs} from 'src/shared/hooks/useInspectionBreadcrumbs';
import {useDeviceListUrlState} from 'src/app/inspections/useDeviceListUrlState';
import {DeficienciesTab} from './components/DeficienciesTab';
import {DevicesTabPanel} from './components/DevicesTabPanel';
import {InspectionRecordHeader} from './components/InspectionRecordHeader';
import {ReopenAuditBanner} from './components/ReopenAuditBanner';
import {ReopenReasonDialog} from './components/ReopenReasonDialog';
import {useGeoTransition} from './useGeoTransition';
import {InspectionDocumentsCard} from '../shared/components/InspectionDocumentsCard';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export const InspectionRecordPage: React.FC = () => {
  const {id} = useParams();
  const inspectionId = id ? Number(id) : undefined;
  const navigate = useAppNavigate();
  const [searchParams] = useSearchParams();

  const {
    search,
    setSearch,
    nodeId,
    setNodeId,
    showOnlyRemaining,
    setShowOnlyRemaining,
  } = useDeviceListUrlState();
  const [reopenDialogOpened, setReopenDialogOpened] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    return tabParam === 'deficiencies' || tabParam === 'documents'
      ? tabParam
      : 'devices';
  });
  const deviceListRef = useRef<HTMLDivElement>(null);

  const navigateToDeviceEntry = (device: InspectionScopeDeviceResponse) => {
    navigate(
      `${PathUtility.InspectionsRecord(inspectionId!, device.deviceId)}?${searchParams.toString()}`
    );
  };

  const {data: inspection, isLoading: isInspectionLoading} =
    useGetInspectionQuery(inspectionId!, {skip: !inspectionId});

  const {breadcrumbs, fallbackTo, fallbackLabel} = useInspectionBreadcrumbs({
    mode: 'view',
    name: inspection?.siteName,
    inspectionId,
  });

  const {data: site} = useGetSiteQuery(inspection?.siteId ?? 0, {
    skip: !inspection,
  });

  const {data: devices, isLoading: isDevicesLoading} =
    useGetInspectionScopeDevicesQuery(inspectionId!, {skip: !inspectionId});

  // Admin/audit surface never records devices — recording only happens
  // through the inspector's guided flow at /dashboard/inspections/my/:id.
  const canRecord = false;

  const isCompleted = inspection?.state === InspectionStateEnum.Completed;

  const {runTransition, isTransitioning} = useGeoTransition(inspectionId ?? 0);

  if (!inspectionId || isInspectionLoading || !inspection) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Inspections}>
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </ContentWrapper>
    );
  }

  const handleViewRemaining = () => {
    setShowOnlyRemaining(true);
    deviceListRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
  };

  const handleReopenConfirm = (reason: string) => {
    setReopenDialogOpened(false);
    void runTransition('reopen', reason);
  };

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Inspections}>
      <Stack gap="sm">
        <BackBar
          fallbackTo={fallbackTo}
          fallbackLabel={fallbackLabel}
          breadcrumbs={breadcrumbs}
          rightSection={<OfflineStatusPill />}
        />

        <InspectionRecordHeader
          inspection={inspection}
          site={site}
          isTransitioning={isTransitioning}
          onSchedule={() => void runTransition('schedule')}
          onReopenRequested={() => setReopenDialogOpened(true)}
          onViewRemaining={handleViewRemaining}
        />

        <ReopenAuditBanner events={inspection.auditEvents} />

        <Tabs value={activeTab} onChange={value => setActiveTab(value ?? 'devices')}>
          <Tabs.List>
            <Tabs.Tab value="devices">Devices</Tabs.Tab>
            {isCompleted && <Tabs.Tab value="documents">Documents</Tabs.Tab>}
            <Tabs.Tab value="deficiencies">Deficiencies</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="devices" pt="md">
            <div ref={deviceListRef}>
              {isDevicesLoading ? (
                <Center py="lg">
                  <Loader size="sm" />
                </Center>
              ) : (
                <Stack gap="md">
                  <DevicesTabPanel
                    devices={devices ?? []}
                    canRecord={canRecord}
                    onEnterDevice={navigateToDeviceEntry}
                    search={search}
                    onSearchChange={setSearch}
                    nodeId={nodeId}
                    onNodeChange={setNodeId}
                    showOnlyRemaining={showOnlyRemaining}
                    onShowOnlyRemainingChange={setShowOnlyRemaining}
                  />
                </Stack>
              )}
            </div>
          </Tabs.Panel>

          {isCompleted && (
            <Tabs.Panel value="documents" pt="md">
              <InspectionDocumentsCard inspectionId={inspectionId} />
            </Tabs.Panel>
          )}

          <Tabs.Panel value="deficiencies" pt="md">
            <DeficienciesTab inspectionId={inspectionId} isCompleted={isCompleted} />
          </Tabs.Panel>
        </Tabs>
      </Stack>

      <ReopenReasonDialog
        opened={reopenDialogOpened}
        onClose={() => setReopenDialogOpened(false)}
        onConfirm={handleReopenConfirm}
        loading={isTransitioning}
      />
    </ContentWrapper>
  );
};

// Device-entry route (InspectionsRecord) is registered by DeviceEntry.page.tsx.
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.InspectionsView(':id'),
  element: <InspectionRecordPage />,
  module: ModuleEnum.Inspections,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
  supportsOffline: true,
});
